<template>
    <b-modal
        id="device-status-modal"
        no-footer
        title="Change device status">

        <!-- Change status -->
        <template v-if="!loadingRevisit">
            <empty-state
                statusIcon="warning"
                statusIconSize="4x"
                title="Changing this device’s status may have an affect on your KPIs"
                subtitle="Please ensure you select the correct status for this device and only update when applicable">
                <slot>
                    <label class="d-block text-left">Select Status</label>
                    <select class="custom-select mb-4" v-model="selectedStatus" @change="handleDeviceStatusSelectChange">
                        <option selected>Open this select menu</option>
                        <option
                            v-for="status in filteredDeviceStatuses"
                            :key="status.key"
                            :value="status.id">
                            {{ status.title }}
                        </option>
                    </select>


                    <div class="alert alert-warning alert-centered rounded-0 mb-4 text-sm" v-if="selectedStatusSendsReplacement">
                        <animated-warning></animated-warning> Selecting this status will result in Likewize sending a replacement device to the customer.<br/>Are you sure this is correct?
                    </div>

                    <label class="d-block text-left" v-if="subStatusIsRequired">Select Sub-Status</label>
                    <select v-if="subStatusIsRequired" class="custom-select mb-4" v-model="selectedSubStatus">
                        <option selected>Open this select menu</option>
                        <option
                            v-for="status in filteredDeviceSubStatuses"
                            :key="status.key"
                            :value="status.id">
                            {{ status.title }}
                        </option>
                    </select>

                    <div v-if="showParts">
                        <label class="d-block text-left" v-if="subStatusIsRequired">Select Parts</label>
                        <multiselect class="z-3" v-model="selectedParts" :options="partsForDevice" :multiple="true" :close-on-select="false" :clear-on-select="false" :hide-selected="true" :preserve-search="true" placeholder="Select Parts" label="title_with_colour" track-by="id" :preselect-first="false">
                            <template slot="tag" slot-scope="props"><span class="custom__tag"><span>{{ props.option.title_with_colour }}</span><span class="custom__remove" @click="props.remove(props.option)">X</span></span></template>
                        </multiselect>
                        <pre class="language-json"><code>{{ value }}</code></pre>
                    </div>

                    <div v-if="showPaymentConfirmationCheckbox" class="d-flex">
                        <input
                            type="checkbox"
                            name="paymentConfirmation"
                            v-model="paymentConfirmed"
                            true-value="true"
                            false-value="false"
                        > <label class="payment-label" for="paymentConfirmation">I confirm that any additional payments required for {{ partsText }} have been taken.</label>
                    </div>

                    <div v-if="showCannotRepairConfirmationCheckbox" class="d-flex">
                        <input
                            type="checkbox"
                            name="cannotRepairConfirmation"
                            v-model="cannotRepairConfirmed"
                            true-value="true"
                            false-value="false"
                        > <label class="payment-label" for="cannotRepairConfirmation">I confirm that I have updated this device to the correct model, make and colour.</label>
                    </div>

                    <div v-if="showPartsInRepairKit">
                        <label class="d-block text-left" v-if="subStatusIsRequired">Please specify</label>
                        <multiselect class="z-3" v-model="selectedPartsInRepairKit" :options="partsInRepairKit" :multiple="true" :close-on-select="false" :clear-on-select="false" :hide-selected="true" :preserve-search="true" placeholder="Select Parts" label="title" track-by="id">
                            <template slot="tag" slot-scope="props"><span class="custom__tag"><span>{{ props.option.title }}</span><span class="custom__remove" @click="props.remove(props.option)">X</span></span></template>
                        </multiselect>
                        <pre class="language-json"><code>{{ value }}</code></pre>
                    </div>

                    <div v-if="showUpload">
                        <label class="d-block text-left">Photo Upload</label>
                        <div class="card-grid grid-col-1">
                            <div class="card-link-container">
                                <input type="file" name="vacantAddressPhoto" id="vacantAddressPhoto" class="file-input" accept="image/*"
                                       capture="environment" @change="uploadPhoto('address-photo', 'vacantAddressPhoto')">
                                <label class="card card-body card-link no-icon text-sm p-3 flex-row bg-gradient-info"
                                       for="vacantAddressPhoto">
                                    <icon-camera
                                        size="md"
                                        class="icon-all-white m-0 mr-2">
                                    </icon-camera>
                                    Upload Photo Of Address
                                </label>
                            </div>
                        </div>
                    </div>

                    <label class="d-block text-left">Enter a brief description of work done <span class="float-right text-sm text-muted">{{ descriptionCharactersRemaining }}</span></label>
                    <textarea v-model="completeDescription" class="form-control" rows="3" maxlength="40" placeholder="Enter a description"></textarea>
                    <p class="text-left text-muted text-xs pl-1">Enter a minimum of 20 characters</p>

                    <p class="text-left text-muted text-sm mt-2" v-if="!isRefurbOrMailIn"><strong>Hint:</strong> If required, the option to book a revisit will appear once you've clicked the Update Status button</p>
                </slot>

                <!-- Revisit bits -->
                <template v-if="!isRefurbOrMailIn">
                    <template v-if="revisitRequired && isOnline">
                        <h2 class="mt-4">Revisit Required</h2>
                        <p>A revisit booking is required for this status change, either click below to book a revisit or change the status.</p>
                        <button type="button" class="btn btn-danger mb-0" @click="bookRevisitLoadJob()">Book Revisit</button>
                    </template>

                    <template v-if="revisitRequired && isOffline">
                        <h2 class="mt-4">Revisit Required</h2>
                        <p>A revisit is required for this order, however you are currently offline.</p>
                    </template>

                    <!--                    <template v-if="revisitRequired && isSelfServe">-->
                    <!--                        <h2 class="mt-4">Revisit Required</h2>-->
                    <!--                        <p>A revisit is required to complete this job, as the customer is not available click the button below to send them a self serve revisit booking form.</p>-->
                    <!--                        <button type="button" class="btn btn-danger mb-0" @click="bookRevisitSelfServe()">Book Revisit</button>-->
                    <!--                    </template>-->
                </template>
                <template v-if="isRefurbOrMailIn && revisitRequired">
                    <div class="alert alert-info mb-0 mt-4">
                        <animated-info></animated-info>
                        <div class="alert-content">Please return this device to the office.</div>
                    </div>
                </template>

            </empty-state>
        </template>

        <!-- Loading revisit -->
        <empty-state
            v-if="loadingRevisit"
            class="bg-transparent"
            statusIcon="loading"
            title="Loading revisit booking"
            subtitle="Please wait">
        </empty-state>

        <template v-slot:modal-footer>
            <button type="button" class="btn btn-light" @click="$bvModal.hide('device-status-modal')">Cancel</button>
            <button type="button" class="btn btn-success" @click="updateDeviceStatus()" :disabled="loadingRevisit || shouldDisableUpdateStatusBtn || settingStatus">{{ buttonText }}</button>
        </template>

        <remove-parts-modal></remove-parts-modal>
    </b-modal>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import Multiselect from 'vue-multiselect'
import Compressor from "compressorjs";

export default {
    props:['jobId', 'deviceId'],
    components: {
        Multiselect
    },
    data() {
        return {
            isSelfServe: false,
            loadingRevisit: false,
            revisitRequired: false,
            selectedStatus: null,
            selectedSubStatus: null,
            completeDescription: '',
            loadingParts: false,
            partsForDevice: [],
            partsInRepairKit: [],
            selectedParts: [],
            selectedPartsInRepairKit: [],
            showParts: false,
            showUpload: false,
            showPaymentConfirmationCheckbox: false,
            paymentConfirmed: false,
            cannotRepairConfirmed: false,
            settingStatus: false
        }
    },

    mounted() {
        this.revisitRequired = false;
        this.loadDeviceStatuses();
        this.fetchAvailablePartsForDevice();
    },

    computed: {
        ...mapGetters([
            "device",
            "deviceParts",
            "deviceStatus",
            "deviceStatuses",
            "selectedDeviceStatusSubStatuses",
            "job",
            "isRefurbOrMailIn",
            "isInHouseTechnician",
            "isSuperAdmin",
            "userService"
        ]),

        filteredDeviceStatuses() {
            return this.deviceStatuses.filter((el) => {
                return (
                    !el.admin_only &&
                    el.title &&
                    (
                        el.title !== "Completed" &&
                        el.title !== "Repaired Pending QC" &&
                        el.title !== "Cancelled Repair"
                    )
                )
            });
        },

        filteredDeviceSubStatuses() {
            return this.selectedDeviceStatusSubStatuses.filter((el) => {
                return (
                    !el.admin_only ||
                    (
                        this.isSuperAdmin ||
                        this.isInHouseTechnician
                    )
                )
            });
        },

        selectedStatusSendsReplacement() {
            if (
                this.job.company_name !== 'Brightstar' &&
                this.job.company_name !== 'Brightstar Bulk' &&
                this.job.company_name !== 'Likewize' &&
                this.job.company_name !== 'Likewize Bulk'
            ) {
                return false;
            }

            for (let i = 0; i < this.filteredDeviceStatuses.length; i++) {
                if (this.filteredDeviceStatuses[i].id === this.selectedStatus) {
                    return this.filteredDeviceStatuses[i].sends_replacement;
                }
            }
            return false;
        },

        subStatusIsRequired() {
            return this.selectedDeviceStatusSubStatuses.length > 0;
        },

        partsText() {
            if(this.selectedParts.length > 1) {
                return "These parts";
            } else {
                return "This part";
            }
        },

        subStatusHasBeenSelected() {
            return this.selectedSubStatus !== null && this.selectedSubStatus !== "Open this select menu";
        },

        showCannotRepairConfirmationCheckbox() {
            if (this.selectedStatus === '43') {
                return this.selectedSubStatus === '96' || this.selectedSubStatus === '97';
            }
            return this.selectedStatus === '13';
        },

        showPartsInRepairKit() {
            if (this.selectedStatus === '2') {
                this.fetchRepairKitPartsForDevice();
                return this.selectedSubStatus === '3' || this.selectedSubStatus === '5';
            }
            return false;
        },

        shouldDisableUpdateStatusBtn() {
            if (!this.selectedStatus || !this.completeDescription || this.completeDescription === '' || this.completeDescription.length < 20) {
                return true;
            }
            if(this.subStatusIsRequired) {
                if(!this.subStatusHasBeenSelected) {
                    return true;
                }
            }

            if (this.selectedStatus === "12") {
                if(this.paymentConfirmed !== "true") {
                    return true;
                }
            }

            if (this.selectedStatus === "13") {
                if(this.cannotRepairConfirmed !== "true") {
                    return true;
                }
            }

            return false;
        },

        descriptionCharactersRemaining() {
            if (!this.completeDescription) return 40;
            let remaining = (40 - this.completeDescription.length);
            if (remaining < 0) {
                return 0;
            }
            return remaining;
        },

        buttonText() {
            if (this.selectedStatus === "22") {
                return "Update status and raise SAW";
            }
            return "Update status";
        }
    },

    watch: {
        // completeDescription(desc) {
        //     this.completeDescription = desc.replace("  ", " ");
        // }
    },

    methods: {
        ...mapActions([
            "loadDeviceStatuses",
            "loadDeviceSubStatuses",
            "loadDeviceStatus",
            "setDeviceComplete",
            "setDeviceNotComplete",
            "displayToast",
            "fetchAvailableParts",
            "fetchPartsInRepairKit",
            "setDeviceStatusDirect"
        ]),

        fetchAvailablePartsForDevice() {
            let that = this;

            this.fetchAvailableParts({deviceId: this.deviceId})
                .then(response => {
                    that.loadingParts = false;
                    that.partsForDevice = response.data.data;
                })
                .catch(response => {
                    this.displayToast({text: response.response.data.messages[0], type: 'error'});
                    that.loadingParts = false;
                });
        },

        fetchRepairKitPartsForDevice() {
            this.fetchPartsInRepairKit({deviceId: this.deviceId, jobId: this.jobId})
                .then(response => {
                    console.log(response);
                    this.partsInRepairKit = response.data.data;
                })
                .catch(response => {
                    this.displayToast({text: response.response.data.messages[0], type: 'error'});
                });
        },

        // canChangeStatusToCustomerNotHome() {
        //   let that = this;
        //
        //   this.canChangeStatus({deviceId: this.deviceId})
        //       .then(response => {
        //         that.loadingParts = false;
        //         that.partsForDevice = response.data.data;
        //         that.canChangeStatus = true;
        //       })
        //       .catch(response => {
        //         this.displayToast({text: response.response.data.messages[0], type: 'error'});
        //         that.loadingParts = false;
        //         that.canChangeStatus = false;
        //       });
        // },

        /** Revisit load job */
        bookRevisitLoadJob() {
            this.loadingRevisit = true;
            this.$http.get(`/api/v4/warranty/booking/build/${this.job.actual_order_id}`)
                .then(res => {
                    localStorage.revisitBookingId = res.data.data.booking_reference;
                    localStorage.revisitDeviceStatus = this.selectedStatus;
                    this.loadingRevisit = false;

                    if (res.data.data.order.devices.length !== 1) {
                        this.$router.push(`/jobs/${this.jobId}/book-revisit/device`);
                        return;
                    }

                    this.$router.push(`/jobs/${this.jobId}/book-revisit`)
                });
        },

        /** Revisit self serve */
        bookRevisitSelfServe() {
            this.loadingRevisit = true;
            this.$http.post(`/api/v4/orders/${this.job.order_id}/revisit-invitation`, {
                "reason": "Sorry we missed you, click below to book a revisit."
            }).then(() => {
                this.loadingRevisit = false;
                this.$bvModal.hide('device-status-modal');
                this.$router.push('job');
            });
        },

        /** Update the device status */
        updateDeviceStatus() {
            if (!this.selectedStatus) return;

            this.revisitRequired = false;

            let revisitRequired = false;
            this.isSelfServe = false;

            this.deviceStatuses.forEach(status => {
                if (status.id === this.selectedStatus) {
                    revisitRequired = status.revisit_required;

                    if (this.selectedStatus == 3 || this.selectedStatus == 4) {
                        this.isSelfServe = true;
                    }
                }
            });

            this.settingStatus = true;

            this.setDeviceStatusDirect({
                jobId:this.jobId,
                deviceId: this.deviceId,
                status: this.selectedStatus,
                subStatusId: this.selectedSubStatus,
                completeDescription: this.completeDescription,
                selectedParts: this.selectedParts,
                missingParts: this.selectedPartsInRepairKit
            }).then(() => {
                this.settingStatus = false;
                // If status is Completed
                if (this.selectedStatus === "1") {
                    this.setDeviceComplete({
                        jobId:this.jobId,
                        deviceId:this.deviceId
                    }).then(() => {
                        this.loadDeviceStatus({
                            statusId: this.device.status_id
                        });
                        this.$bvModal.hide('device-status-modal');
                    });
                    // If status is NOT Completed
                } else if (this.selectedStatus !== "1") {
                    this.setDeviceNotComplete({
                        jobId:this.jobId,
                        deviceId:this.deviceId
                    }).then(() => {
                        // @TODO - Remove for general SAW Request release
                        // @TODO - Disabled at time of commit.
                        //   if (this.userService.details.id === 160 ||
                        //     this.userService.details.id === 201 ||
                        //     this.userService.details.id === 363 ||
                        //     this.userService.details.id === 855 ||
                        //     this.userService.details.id === 1125 ||
                        //     this.userService.details.id === 1276
                        //   ) {
                        //     if (this.selectedStatus === "22") {
                        //       // Go raise a SAW
                        //       return this.$router.push(`/saw/request/${this.deviceId}/job/${this.jobId}`);
                        //     }
                        //   }

                        this.loadDeviceStatus({
                            statusId: this.device.status_id
                        });

                        if (revisitRequired) {
                            this.revisitRequired = true;
                        } else {
                            this.$bvModal.hide('device-status-modal');
                        }
                    });
                }
            }).catch((error) => {
                this.displayToast({text: error.data.messages[0], type: 'error', duration: 20000});
                this.settingStatus = false;
            });
        },

        handleDeviceStatusSelectChange() {
            if (this.selectedStatus === '3') {
                this.showUpload = true;

            } else if (this.selectedStatus === '9' && this.deviceParts.length > 0) {
                this.$bvModal.show('remove-parts-modal');

            } else if (this.selectedStatus === '12') {
                this.fetchAvailablePartsForDevice();
                this.showParts = true;
                this.showPaymentConfirmationCheckbox = true;

            } else {
                this.showParts = false;
                this.showPaymentConfirmationCheckbox = false;
                this.showUpload = false;
            }

            // reset the selected sub status
            this.selectedSubStatus = null;

            this.loadDeviceSubStatuses({
                statusId: this.selectedStatus
            });
        },

        uploadPhoto(type, elementId) {
            const filePicker = document.getElementById(elementId);

            if (!filePicker.files || filePicker.files.length <= 0) {
                this.displayToast({text: 'No file selected.', type: 'error'});
                return;
            }

            let self = this;
            new Compressor(filePicker.files[0], {
                quality: 0.6,
                mimeType: 'image/jpeg',
                maxWidth: 1920,
                maxHeight: 1920,
                success(result) {

                    const data = new FormData();
                    data.append('image', result);
                    data.set('device_id', self.deviceId);
                    data.set('type', 'customer-not-home');

                    self.displayToast({text: 'File is being uploaded.', type: 'success'});

                    self.$http.post(
                        '/api/v4/upload-image',
                        data,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        },
                    ).catch((error) => {
                        console.log(error);
                        const message = error?.response?.statusText ?? 'Error uploading file';
                        self.displayToast({text: message, type: 'error'});
                    });

                },
                error() {
                    self.displayToast({text: 'Could not parse file.', type: 'error'});
                    filePicker.value = "";
                },
            });
        }
    }
}
</script>

<style scoped>
.input-group .input-group-prepend {
    left: 15px;
    top: 15px;
    color: #b7baca;
}

.payment-label {
    font-size: 9.5px !important;
    padding-top: 10px;
    margin-left: 7px;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
